import { useCallback, useEffect, useRef, useState } from 'react';

import type { CurrentUser, LtPlusBillingInterval } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import type { AnalyticsHandler } from '../../SignupDrawer/hooks/useSignupFlow';
import { DEFAULT_LT_PLUS_BILLING_INTERVAL } from '../ltPlusPlans';

export type LtPlusStep = 'benefits' | 'checkout' | 'confirmation';

type LocalLtPlusUser = {
  isAuthenticated: boolean;
  hasMembership: boolean;
};

type UseLtPlusFlowOptions = {
  initialUser: CurrentUser;
  initialStep?: LtPlusStep;
  onComplete: () => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseLtPlusFlowReturn = {
  step: LtPlusStep;
  localUser: LocalLtPlusUser;
  showSignInButton: boolean;
  selectedBillingInterval: LtPlusBillingInterval;
  setSelectedBillingInterval: (billingInterval: LtPlusBillingInterval) => void;
  navigateToCheckout: () => void;
  navigateBackToBenefits: () => void;
  handlePaymentSuccess: () => void;
  handleAuthSuccess: (user: CurrentUser) => void;
  handleDone: () => void;
};

const getNormalizedUser = (user: CurrentUser): LocalLtPlusUser => ({
  isAuthenticated: !!user?.isAuthenticated,
  hasMembership: !!user?.hasPremiumMembership,
});

/**
 * Orchestrates LT Plus drawer flow state and local user changes across steps.
 */
export const useLtPlusFlow = ({
  initialUser,
  initialStep = 'benefits',
  onComplete,
  onAnalyticsEvent,
}: UseLtPlusFlowOptions): UseLtPlusFlowReturn => {
  const [step, setStep] = useState<LtPlusStep>(initialStep);
  const [localUser, setLocalUser] = useState<LocalLtPlusUser>(() => getNormalizedUser(initialUser));
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<LtPlusBillingInterval>(
    DEFAULT_LT_PLUS_BILLING_INTERVAL
  );
  const hasCompletedRef = useRef(false);

  const handleDone = useCallback(() => {
    if (hasCompletedRef.current) {
      return;
    }

    hasCompletedRef.current = true;
    onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_FLOW_COMPLETED, {
      [ANALYTICS_KEYS.STEP]: step,
      [ANALYTICS_KEYS.WAS_AUTHENTICATED]: localUser.isAuthenticated,
      [ANALYTICS_KEYS.HAD_MEMBERSHIP]: localUser.hasMembership,
    });
    onComplete();
  }, [localUser.hasMembership, localUser.isAuthenticated, onAnalyticsEvent, onComplete, step]);

  const navigateToCheckout = useCallback(() => {
    setStep('checkout');
  }, []);

  const navigateBackToBenefits = useCallback(() => {
    setStep('benefits');
  }, []);

  const handlePaymentSuccess = useCallback(() => {
    setStep('confirmation');
  }, []);

  const handleAuthSuccess = useCallback(
    (user: CurrentUser) => {
      const nextUser = getNormalizedUser(user);
      setLocalUser(nextUser);

      onAnalyticsEvent?.(BUCK_EVENTS.LT_PLUS_AUTH_SUCCESS, {
        [ANALYTICS_KEYS.IS_AUTHENTICATED]: nextUser.isAuthenticated,
        [ANALYTICS_KEYS.HAS_MEMBERSHIP]: nextUser.hasMembership,
      });

      if (nextUser.hasMembership) {
        handleDone();
        return;
      }

      setStep('checkout');
    },
    [handleDone, onAnalyticsEvent]
  );

  useEffect(() => {
    if (localUser.isAuthenticated && localUser.hasMembership) {
      handleDone();
    }
  }, [handleDone, localUser.hasMembership, localUser.isAuthenticated]);

  return {
    step,
    localUser,
    showSignInButton: !localUser.isAuthenticated,
    selectedBillingInterval,
    setSelectedBillingInterval,
    navigateToCheckout,
    navigateBackToBenefits,
    handlePaymentSuccess,
    handleAuthSuccess,
    handleDone,
  };
};
